import {
  createListAllQuery,
  getTaskList,
  GetTaskListQuery,
  GetTaskListQueryVariables,
  graphqlCall,
  listTaskLists,
  ListTaskListsQueryVariables,
  TaskList,
  tasklistByHabitat,
  TasklistByHabitatQueryVariables,
} from '@akin-home/amplify';
import { getAuthorizedAPIOptions } from '@akin-home/auth/frontend';
import {
  checkListGraphqlToModel,
  ChecklistModel,
  formatChecklistsQueryResponse,
} from '@akin-home/checklists';
import { objectToQueryParams } from '@akin-home/objects';
import { createQueryKeySet } from '@akin-home/utils/react-query';
import { API } from 'aws-amplify';
import { useQuery } from 'react-query';

export const checklistKeys = createQueryKeySet({
  key: 'checklists',
});

export const listAllTaskListsQuery = createListAllQuery<
  TaskList,
  ListTaskListsQueryVariables
>(listTaskLists, 'listTaskLists');

export const listAllTaskListsByHabitatQuery = createListAllQuery<
  TaskList,
  TasklistByHabitatQueryVariables
>(tasklistByHabitat, 'tasklistByHabitat');

type ListChecklistsQueryParams = {
  query?: {
    title?: string;
    confidence?: string;
  };
};

export const listAllChecklistsQuery = async (
  params: ListChecklistsQueryParams
) => {
  const query_string = objectToQueryParams(params.query);

  try {
    const result = await API.get(
      'ChecklistAPI',
      '/checklists?' + query_string,
      await getAuthorizedAPIOptions()
    );

    return result.items;
  } catch (ex) {
    console.log(ex);
    return [];
  }
};

export const listAllTaskListModelsQuery = async (
  params?: ListTaskListsQueryVariables
) => {
  return formatChecklistsQueryResponse(await listAllTaskListsQuery(params));
};

export const getTaskListQuery = (params?: GetTaskListQueryVariables) =>
  graphqlCall<GetTaskListQuery>(getTaskList, params);

export const getMinimalTaskList = async (id: string) => {
  return await graphqlCall<{
    getTaskList?: { checklist?: string; id: string };
  }>(
    `
query MyQuery($id: ID!) {
  getTaskList(id: $id) {
    checklist
    id
  }
}
`,
    { id }
  );
};

export const useGetTaskListQuery = (id?: string, options?: any) =>
  useQuery(
    checklistKeys.detail(id),
    async () => (await getTaskListQuery({ id })).data.getTaskList as TaskList,
    {
      enabled: !!id,
      select: checkListGraphqlToModel,
    }
  );

export const useGetTaskList = (params?: { id?: string; options?: any }) => {
  const { id } = params || {};

  const { data: list, ...results } = useGetTaskListQuery(id);

  return {
    list,
    ...results,
  };
};

export const useListAllTaskListsQuery = (
  params?: ListTaskListsQueryVariables,
  options?: any
) =>
  useQuery(
    params ? checklistKeys.list(params) : checklistKeys.lists(),
    () => listAllTaskListsQuery(params),
    {
      select: formatChecklistsQueryResponse,
      ...options,
    }
  );

export const useListTaskListsByHabitatQuery = (
  params?: TasklistByHabitatQueryVariables,
  options?: any
) =>
  useQuery<TaskList[], unknown, ChecklistModel[]>(
    params ? checklistKeys.list(params) : checklistKeys.lists(),
    () => listAllTaskListsByHabitatQuery(params),
    {
      enabled: !!params?.habitatID,
      select: formatChecklistsQueryResponse,
      ...options,
    }
  );

export const useListTaskListsByHabitat = (
  params: TasklistByHabitatQueryVariables
) => {
  const { data: lists = [], ...result } =
    useListTaskListsByHabitatQuery(params);

  return {
    ...result,
    lists,
  };
};
